import React, { useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";

// Socket instance (use your existing Socket.config)
import Socket from "../utils/Socket.config";

// API (create these)
import { ReservationFetchAPI } from "../../api/reservation";
import { MailFetchAPI } from "../../api/Mail";

const PANAMA_TZ = "America/Panama";



/* ── iconos ── */
const IcoCal = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 6h10M11 12h10M11 18h10" />
    <path d="M3 6l1.5 1.5L7 5" />
    <path d="M3 12l1.5 1.5L7 11" />
    <path d="M3 18l1.5 1.5L7 17" />
  </svg>
); const IcoX = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>);

const statusBadge = (status) => {
  const s = String(status || "").toUpperCase();
  const map = {
    PENDING: "warning",
    CONFIRMED: "success",
    REJECTED: "danger",
    CANCELLED: "secondary",
  };
  return map[s] || "light";
};

export default function ModalReservation({
  business_code,
  branchoffice_code,
  updateTable,
  tables = [], // for table assignment, format: [{ id, name }]
}) {
  const [show, setShow] = useState(false);

  // Data
  const [reservations, setReservations] = useState([]);

  // Filters
  const [onlyPending, setOnlyPending] = useState(false);
  const [from, setFrom] = useState(moment().tz(PANAMA_TZ).format("YYYY-MM-DD"));
  const [to, setTo] = useState(moment().tz(PANAMA_TZ).format("YYYY-MM-DD"));
  // UI signals
  const [unreadCount, setUnreadCount] = useState(0);
  const [flash, setFlash] = useState(false);
  const [loading, setLoading] = useState(false);

  const [selectedReservation, setSelectedReservation] = useState(null);
  const [tableName, setTableName] = useState("");
  const [selectedTableId, setSelectedTableId] = useState("");

  const visibleReservations = useMemo(() => {
    if (!onlyPending) return reservations;
    return reservations.filter((r) => String(r.status).toUpperCase() === "PENDING");
  }, [reservations, onlyPending]);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      let token = localStorage.getItem("x-access-machine-token");
      const params = {
        business_id: JSON.parse(localStorage.getItem("business")).id,
        branchoffice_id: JSON.parse(localStorage.getItem("branchoffice")).id,
        from: moment.tz(from, "YYYY-MM-DD", PANAMA_TZ).startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        to: moment.tz(to, "YYYY-MM-DD", PANAMA_TZ).endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      };

      // Optional server-side status filter
      if (onlyPending) params.status = "PENDING";

      const resp = await ReservationFetchAPI.getReservationsClients(
        params.branchoffice_id,
        params.business_id,
        params.from,
        params.to,
        token
      );
      console.log("Fetched reservations:", resp);
      if (!resp?.data.success) {
        setReservations([]);
        return;
      }

      // Keep local list sorted by reserved time
      const sorted = (resp.data.reservations || []).slice().sort((a, b) => {
        const da = new Date(a.reserved_at).getTime();
        const db = new Date(b.reserved_at).getTime();
        return da - db;
      });

      console.log("Sorted reservations:", sorted);

      setReservations(sorted);
    } catch (e) {
      console.error("Failed to fetch reservations:", e);
      setReservations([]);
    } finally {
      setLoading(false);
    }
  };

  const open = async () => {
    setShow(true);
    setUnreadCount(0); // Mark as seen (simple approach)
    await fetchReservations();
  };

  //Hook Socket 
  useEffect(() => {
    // Connection with the sockets server
    const socket = Socket({ "business_code": business_code, "branchoffice_code": branchoffice_code });

    socket.on("new_reservation", (reservation) => {
      // Visual flash + badge increment
      setUnreadCount((c) => c + 1);
      setFlash(true);
      setTimeout(() => setFlash(false), 1200);

      // If modal is open, insert/update list live
      setReservations((prev) => {
        const next = [...prev];
        const id = reservation?.id;
        const idx = next.findIndex((r) => r.id === id);
        if (idx >= 0) next[idx] = { ...next[idx], ...reservation };
        else next.unshift(reservation);
        return next;
      });

    });

    return () => {
      socket.disconnect();
    };
  }, []);

  /**
   * Update reservation status
   */
  const updateStatus = async (reservationId, status) => {
    let token = localStorage.getItem("x-access-machine-token");
    let branchoffice = JSON.parse(localStorage.getItem("branchoffice"));
    let generalData = JSON.parse(localStorage.getItem("generalData"));
    // status: CONFIRMED | REJECTED | CANCELLED
    try {
      const resp = await ReservationFetchAPI.putReservationClient(reservationId, { status }, token);
      if (!resp?.data.success) return;
      // Optionally send email notification
      try {
        let emailData = {
          "email": resp.data.reservation.guest_email,
          "branchoffice_name": branchoffice.name,
          "business_color": generalData[0].data.configuration.containerColor || "#000000",
          "business_name": generalData[0].data.business.name,
          "code": resp.data.reservation.code,
          "guest_name": resp.data.reservation.guest_name,
          "guest_phone": resp.data.reservation.guest_phone,
          "logo_url": generalData[0].data.business.logo,
          "notes": resp.data.reservation.notes,
          "reserved_at": resp.data.reservation.reserved_at
        }

        if (status === "CONFIRMED") {
          emailData.template = "restaurant-reservation-confirmation"
          emailData.subject = "Confirmación de Reserva"
        } else if (status === "REJECTED") {
          emailData.template = "restaurant-reservation-rejection"
          emailData.subject = "Rechazo de Reserva"
        } else {
          emailData.template = "restaurant-reservation-cancelled"
          emailData.subject = "Cancelación de Reserva"
        }


        await MailFetchAPI.sendMail(emailData, token);
      } catch (e) {
        console.error("Failed to send email notification:", e);
      }
      setReservations((prev) =>
        prev.map((r) =>
          r.id === reservationId ? { ...r, status } : r
        )
      );
    } catch (e) {
      // Handle error alert in your style if needed
      console.error(e);
    }
  };

  return (
    <>
      <button
        className={"rsv-trigger" + (flash ? " is-flash" : "")}
        onClick={open}
      >
        <IcoCal />
        <span className="rsv-trigger-label">Reservaciones</span>
        {unreadCount > 0 && (
          <span className="rsv-trigger-badge">{unreadCount}</span>
        )}
      </button>


      {show && (
        <div className="rsv-x-overlay" onClick={() => setShow(false)}>
          <div className="rsv-x-modal" onClick={(e) => e.stopPropagation()}>
            {/* cabecera */}
            <div className="rsv-x-head">
              <div className="rsv-x-head-titles">
                <h2>Gestión de reservas</h2>
                <p>Consulta, asigna mesas y gestiona las reservas</p>
              </div>
              <button className="rsv-x-close" aria-label="Cerrar" onClick={() => setShow(false)}>
                <IcoX />
              </button>
            </div>

            {/* cuerpo 35/65 ; en móvil, show-detail cuando hay selección */}
            <div className={"rsv-x-body" + (selectedReservation ? " show-detail" : "")}>

              {/* ── PANEL DETALLE (izquierda) ── */}
              <div className="rsv-x-detail">
                <button
                  className="rsv-x-back"
                  type="button"
                  onClick={() => { setSelectedReservation(null); setTableName(""); setSelectedTableId(""); }}
                >
                  Regresar a Reservas
                </button>

                {!selectedReservation ? (
                  <div className="rsv-x-empty-detail">
                    <div className="t">Ninguna reserva seleccionada</div>
                    <div className="s">Selecciona una reserva de la lista para ver sus detalles.</div>
                  </div>
                ) : (
                  <>
                    <div className="rsv-x-sectlabel">Reserva seleccionada</div>
                    <div className="rsv-x-detail-name">
                     Cliente: {selectedReservation.guest_name || selectedReservation.client_name || "Sin nombre"}
                    </div>
                    <div className="rsv-x-detail-meta">
                     Fecha y hora: {moment(selectedReservation.reserved_at).tz(PANAMA_TZ).format("DD/MM HH:mm")} 
                     {/* · {selectedReservation.people_qty} personas */}
                    </div>
                    <div className="rsv-x-detail-status">
                      <span className={"rsv-x-status " + statusBadge(selectedReservation.status)}>
                        {String(selectedReservation.status || "").toUpperCase()}
                      </span>
                    </div>

                    {/* MESA — acción principal */}
                    <div className="rsv-x-table-block">
                      <div className="rsv-x-sectlabel">Mesa</div>
                      <div className={"rsv-x-current-table" + (selectedReservation?.table?.name ? "" : " is-none")}>
                        {selectedReservation?.table?.name || "Sin asignar"}
                      </div>

                      <select
                        className="rsv-x-select"
                        value={selectedTableId}
                        onChange={(e) => setSelectedTableId(e.target.value)}
                      >
                        <option value="">Seleccionar mesa...</option>
                        {tables
                          .filter((t) => !t.is_archived)
                          .sort((a, b) => String(a.name).localeCompare(String(b.name), "es", { numeric: true }))
                          .map((t) => (
                            <option key={t.id} value={String(t.id)}>
                              Mesa {t.number}
                            </option>
                          ))}
                      </select>

                      <button
                        className="rsv-x-btn primary block"
                        disabled={!String(selectedTableId || "").trim()}
                        onClick={async () => {
                          updateTable(Number(selectedTableId), {
                            reservation_id: selectedReservation.id,
                            status: "reserved",
                          });

                          const found = tables.find(t => String(t.id) === String(selectedTableId));
                          setReservations((prev) =>
                            prev.map((x) =>
                              x.id === selectedReservation.id
                                ? { ...x, table: { id: selectedTableId, name: found?.number || "Desconocida" } }
                                : x
                            )
                          );
                        }}
                      >
                        {selectedReservation?.table?.name ? "Cambiar mesa" : "Asignar mesa"}
                      </button>
                    </div>

                    {/* INFORMACIÓN secundaria */}
                    <div className="rsv-x-info">
                      <div className="rsv-x-info-item">
                        <div className="k">Teléfono</div>
                        <div className="v">{selectedReservation.guest_phone || "-"}</div>
                      </div>
                      <div className="rsv-x-info-item">
                        <div className="k">Personas</div>
                        <div className="v">{selectedReservation.people_qty} personas</div>
                      </div>
                      {selectedReservation.notes ? (
                        <div className="rsv-x-info-item">
                          <div className="k">Notas</div>
                          <div className="rsv-x-note">{selectedReservation.notes}</div>
                        </div>
                      ) : null}
                    </div>
                  </>
                )}
              </div>

              {/* ── PANEL LISTA (derecha) ── */}
              <div className="rsv-x-list-pane">
                <div className="rsv-x-list-top">
                  <div className="rsv-x-list-head">
                    <h3>Reservas</h3>
                    <span className="rsv-x-list-count">
                      <b>{visibleReservations.length}</b> reserva{visibleReservations.length === 1 ? "" : "s"}
                    </span>
                  </div>

                  <div className="rsv-x-filters">
                    <div className="rsv-x-field">
                      <label>Desde</label>
                      <input className="rsv-x-input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                    </div>
                    <div className="rsv-x-field">
                      <label>Hasta</label>
                      <input className="rsv-x-input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                    </div>
                    <button className="rsv-x-btn primary" onClick={fetchReservations} disabled={loading}>
                      {loading ? "Cargando..." : "Buscar"}
                    </button>
                  </div>
                </div>

                <div className="rsv-x-list">
                  {visibleReservations.length === 0 ? (
                    <div className="rsv-x-list-empty">No hay reservas en este rango.</div>
                  ) : (
                    visibleReservations.map((r) => {
                      const isSel = selectedReservation?.id === r.id;
                      const isPending = String(r.status).toUpperCase() === "PENDING";
                      return (
                        <div
                          key={r.id}
                          className={"rsv-x-item" + (isSel ? " is-selected" : "")}
                          onClick={() => {
                            setSelectedReservation(r);
                            const currentTableId = r?.table?.id || r?.table_id || "";
                            setSelectedTableId(String(currentTableId || ""));
                          }}
                        >
                          <div className="rsv-x-item-time">
                            {moment(r.reserved_at).tz(PANAMA_TZ).format("HH:mm")}
                          </div>

                          <div className="rsv-x-item-main">
                            <div className="rsv-x-item-name">{r.guest_name || r.client_name || "Sin nombre"}</div>
                            <div className="rsv-x-item-sub">
                              {(r.guest_phone || "—")} · {r.people_qty} personas
                            </div>
                          </div>

                          <div className="rsv-x-item-right">
                            <span className="rsv-x-item-tablev">Mesa: <b>{r?.table?.name || "Sin asignar"}</b></span>
                            <span className={"rsv-x-status " + statusBadge(r.status)}>
                              {String(r.status || "").toUpperCase()}
                            </span>
                          </div>

                          {/* acciones: solo en la fila seleccionada */}
                          {isSel ? (
                            <div className="rsv-x-item-actions" onClick={(e) => e.stopPropagation()}>
                              <button className="rsv-x-btn sm success" disabled={!isPending} onClick={() => updateStatus(r.id, "CONFIRMED")}>
                                Confirmar
                              </button>
                              <button className="rsv-x-btn sm danger" disabled={!isPending} onClick={() => updateStatus(r.id, "REJECTED")}>
                                Rechazar
                              </button>
                              <button
                                className="rsv-x-btn sm ghost"
                                disabled={["CANCELLED", "REJECTED"].includes(String(r.status).toUpperCase())}
                                onClick={() => updateStatus(r.id, "CANCELLED")}
                              >
                                Cancelar
                              </button>
                            </div>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* footer */}
            <div className="rsv-x-foot">
              <button className="rsv-x-btn ghost" onClick={() => setShow(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}